<template>
  <div class="form-section mb-0">

    <!-- Cuenta de haberes -->
    <div class="row">
      <div class="col-md-12">
        <h2 class="form-subtitle">Cuenta de haberes</h2>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Tipo de documento <span v-if="hasBankSelected" class="text-danger">*</span></label>
          <FormSelect
            id="bankIdentifierType"
            v-model="form.bankIdentifierType"
            :items="identifierTypes"
            defaultOption="Elegir tipo"
            :disabled="true"
            :showError="v.bankIdentifierType && v.bankIdentifierType.$error"
          />
          <FormError
            v-if="v.bankIdentifierType && v.bankIdentifierType.$error && !v.bankIdentifierType.required"
            message="Tipo de documento es requerido"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Número de documento <span v-if="hasBankSelected" class="text-danger">*</span></label>
          <input
            type="text"
            :value="form.bankIdentifier"
            disabled
            class="custom-form-control"
            :class="{ 'custom-form-control-error': v.bankIdentifier && v.bankIdentifier.$error }"
          />
          <FormError
            v-if="v.bankIdentifier && v.bankIdentifier.$error && !v.bankIdentifier.required"
            message="Número de documento es requerido"
          />
        </div>
      </div>

      <div class="col-md-12">
        <div class="form-group">
          <label>Titular de la cuenta <span v-if="hasBankSelected" class="text-danger">*</span></label>
          <input
            type="text"
            :value="form.bankHolder"
            disabled
            class="custom-form-control"
            :class="{ 'custom-form-control-error': v.bankHolder && v.bankHolder.$error }"
          />
          <FormError
            v-if="v.bankHolder && v.bankHolder.$error && !v.bankHolder.required"
            message="Titular de la cuenta es requerido"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Banco</label>
          <FormSelect
            id="bankId"
            v-model="form.bankId"
            :items="banks"
            defaultOption="Elegir banco"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Tipo de cuenta <span v-if="hasBankSelected" class="text-danger">*</span></label>
          <FormSelect
            id="accountType"
            v-model="form.accountType"
            :items="accountTypes"
            defaultOption="Elegir tipo de cuenta"
            :showError="v.accountType && v.accountType.$error"
          />
          <FormError
            v-if="v.accountType && v.accountType.$error && !v.accountType.required"
            message="Tipo de cuenta es requerido"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Número de cuenta <span v-if="hasBankSelected" class="text-danger">*</span></label>
          <input
            type="text"
            v-model.trim="form.bankAccount"
            autocomplete="off"
            placeholder="Ingresar número de cuenta"
            maxlength="20"
            class="custom-form-control"
            :class="{ 'custom-form-control-error': v.bankAccount && v.bankAccount.$error }"
          />
          <FormError
            v-if="v.bankAccount && v.bankAccount.$error && !v.bankAccount.required"
            message="Número de cuenta es requerido"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>CCI</label>
          <input
            type="text"
            v-model.trim="form.bankInterAccount"
            autocomplete="off"
            placeholder="Ingresar CCI (20 dígitos)"
            maxlength="20"
            class="custom-form-control"
            :class="{ 'custom-form-control-error': v.bankInterAccount && v.bankInterAccount.$error }"
          />
          <FormError
            v-if="v.bankInterAccount && v.bankInterAccount.$error && !v.bankInterAccount.numeric"
            message="CCI debe ser numérico"
          />
          <FormError
            v-if="v.bankInterAccount && v.bankInterAccount.$error && !v.bankInterAccount.minLength"
            message="CCI debe tener 20 dígitos"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Moneda</label>
          <FormSelect
            id="salaryCurrency"
            v-model="form.salaryCurrency"
            :items="currencyList"
            defaultOption="Elegir moneda"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group d-flex align-items-center mt-2">
          <label class="mb-0 mr-3">Datos bancarios confirmados</label>
          <b-form-checkbox
            v-model="form.bankAccountVerified"
            name="bankAccountVerified"
            switch
            size="lg"
          />
        </div>
      </div>

      <div class="col-md-6" v-if="form.bankAccountVerified">
        <div class="form-group mt-2">
          <label>Confirmado por</label>
          <input
            type="text"
            :value="form.bankAccountVerifiedBy"
            disabled
            class="custom-form-control"
          />
        </div>
      </div>
    </div>

    <!-- CTS -->
    <div class="row mt-3">
      <div class="col-md-12">
        <h2 class="form-subtitle">CTS</h2>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Banco CTS</label>
          <FormSelect
            id="ctsBankId"
            v-model="form.ctsBankId"
            :items="banks"
            defaultOption="Elegir banco CTS"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Cuenta CTS</label>
          <input
            type="text"
            v-model.trim="form.ctsAccount"
            autocomplete="off"
            placeholder="Ingresar cuenta CTS"
            class="custom-form-control"
          />
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group mb-0">
          <label>Moneda CTS</label>
          <FormSelect
            id="ctsCurrency"
            v-model="form.ctsCurrency"
            :items="currencyList"
            defaultOption="Elegir moneda"
          />
        </div>
      </div>
    </div>

    <!-- Sistema pensionario -->
    <div class="row mt-3">
      <div class="col-md-12">
        <h2 class="form-subtitle">Sistema pensionario</h2>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label>Sistema previsional</label>
          <FormSelect
            id="pensionSystem"
            v-model="form.pensionSystem"
            :items="pensionSystemList"
            defaultOption="Elegir sistema"
          />
        </div>
      </div>

      <template v-if="form.pensionSystem === 'AFP'">
        <div class="col-md-6">
          <div class="form-group">
            <label>Entidad AFP</label>
            <SearchableDropdown
              placeholder="Elegir AFP"
              :options="pensionEntities"
              :oldValue="form.pensionEntityId"
              @selected="form.pensionEntityId = $event"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-group">
            <label>Código CUSPP</label>
            <input
              type="text"
              v-model="form.cusppCode"
              autocomplete="off"
              placeholder="Ingresar código CUSPP"
              class="custom-form-control"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-group">
            <label>Tipo de comisión AFP</label>
            <FormSelect
              id="afpCommissionType"
              v-model="form.afpCommissionType"
              :items="afpCommissionTypeList"
              defaultOption="Elegir tipo"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Tributario -->
    <div class="row mt-3">
      <div class="col-md-12">
        <h2 class="form-subtitle">Tributario</h2>
      </div>

      <div class="col-md-6">
        <div class="form-group mb-0">
          <label>Situación 5ta categoría</label>
          <FormSelect
            id="fifthCategoryStatus"
            v-model="form.fifthCategoryStatus"
            :items="fifthCategoryStatusList"
            defaultOption="Elegir situación"
          />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { FormError, FormSelect, SearchableDropdown } from 'wize-admin'
import { Constants } from '@/core/utils'

export default {
  components: { FormError, FormSelect, SearchableDropdown },
  props: {
    form:            { type: Object, required: true },
    v:               { type: Object, required: true },
    banks:           { type: Array,  default: () => [] },
    pensionEntities: { type: Array,  default: () => [] },
  },
  data() {
    return {
      identifierTypes: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
        Constants.IdentifierType.LIBRETA_MILITAR,
        Constants.IdentifierType.CARNET_MILITAR,
      ],
      accountTypes: [
        Constants.AccountType.SAVINGS,
        Constants.AccountType.CURRENT,
        Constants.AccountType.SALARY,
      ],
      currencyList:           Object.values(Constants.SalaryCurrency),
      pensionSystemList:      Object.values(Constants.PensionSystem),
      afpCommissionTypeList:  Object.values(Constants.AfpCommissionType),
      fifthCategoryStatusList: Object.values(Constants.FifthCategoryStatus),
    }
  },
  computed: {
    hasBankSelected() {
      return !!this.form.bankId
    },
  },
}
</script>

<style lang="stylus" scoped></style>
